import { Howl, Howler } from 'howler'

// ── Volume levels ──
const VOLUMES = {
  master: 1.0,
  sfx: 0.8,
  music: 0.4,
  ambient: 0.3,
}

// ── Audio instances ──
const sfxCache = new Map<string, Howl>()
let currentMusic: Howl | null = null
let currentAmbient: Howl | null = null
let initialized = false

// ── Init — call once after first user interaction ──
export function initAudio(): void {
  if (initialized) return
  initialized = true
  const settings = getAudioSettings()
  Object.assign(VOLUMES, settings)
  Howler.volume(VOLUMES.master)
}

// ── Internal helpers ──
function getSFX(path: string): Howl {
  if (sfxCache.has(path)) return sfxCache.get(path)!
  const sound = new Howl({ src: [path], volume: VOLUMES.sfx, preload: true })
  sfxCache.set(path, sound)
  return sound
}

function audioEnabled(): boolean {
  return getAudioSettings().master > 0
}

// ── SFX ──
export function playSFX(name: SFXName): void {
  if (!audioEnabled()) return
  const path = `/audio/sfx/${SFX_PATHS[name]}`
  getSFX(path).play()
}

// ── Music — cross-fade ──
export function playMusic(name: MusicName): void {
  if (!audioEnabled()) return
  const path = `/audio/music/${MUSIC_PATHS[name]}`
  if (currentMusic) {
    const old = currentMusic
    old.fade(VOLUMES.music, 0, 1000)
    setTimeout(() => old.stop(), 1000)
  }
  currentMusic = new Howl({ src: [path], loop: true, volume: 0 })
  currentMusic.play()
  currentMusic.fade(0, VOLUMES.music, 1000)
}

export function stopMusic(): void {
  if (!currentMusic) return
  const old = currentMusic
  old.fade(VOLUMES.music, 0, 800)
  setTimeout(() => old.stop(), 800)
  currentMusic = null
}

// ── Ambient — cross-fade ──
export function playAmbient(zoneName: string): void {
  if (!audioEnabled()) return
  const path = `/audio/sfx/zone/${zoneName}-ambient.mp3`
  if (currentAmbient) {
    const old = currentAmbient
    old.fade(VOLUMES.ambient, 0, 2000)
    setTimeout(() => old.stop(), 2000)
  }
  currentAmbient = new Howl({ src: [path], loop: true, volume: 0 })
  currentAmbient.play()
  currentAmbient.fade(0, VOLUMES.ambient, 2000)
}

export function stopAmbient(): void {
  if (!currentAmbient) return
  const old = currentAmbient
  old.fade(VOLUMES.ambient, 0, 1500)
  setTimeout(() => old.stop(), 1500)
  currentAmbient = null
}

// ── Stop all ──
export function stopAll(): void {
  Howler.stop()
  currentMusic = null
  currentAmbient = null
}

// ── Volume controls ──
export function setMasterVolume(v: number): void {
  VOLUMES.master = v
  Howler.volume(v)
  saveAudioSettings({ master: v })
}

export function setMusicVolume(v: number): void {
  VOLUMES.music = v
  currentMusic?.volume(v)
  saveAudioSettings({ music: v })
}

export function setSFXVolume(v: number): void {
  VOLUMES.sfx = v
  saveAudioSettings({ sfx: v })
}

export function setAmbientVolume(v: number): void {
  VOLUMES.ambient = v
  currentAmbient?.volume(v)
  saveAudioSettings({ ambient: v })
}

export function getVolumes() {
  return { ...VOLUMES }
}

// ── Persistence ──
function saveAudioSettings(settings: Partial<typeof VOLUMES>): void {
  const current = getAudioSettings()
  localStorage.setItem('ab_audio', JSON.stringify({ ...current, ...settings }))
}

function getAudioSettings(): typeof VOLUMES {
  try {
    const stored = localStorage.getItem('ab_audio')
    if (stored) return { ...VOLUMES, ...JSON.parse(stored) }
  } catch {}
  return { ...VOLUMES }
}

// ── Type definitions ──
export type SFXName =
  | 'click' | 'hover' | 'modal-open' | 'modal-close'
  | 'nav-switch' | 'notification' | 'error'
  | 'hit-fire' | 'hit-water' | 'hit-earth' | 'hit-air'
  | 'hit-void' | 'hit-aether'
  | 'super-effective' | 'not-effective' | 'ko' | 'flee'
  | 'creature-catch' | 'coin-collect' | 'level-up'
  | 'awakening' | 'evolution' | 'trade-complete' | 'bounty-appear'
  | 'vo-fight' | 'vo-ready' | 'vo-victory' | 'vo-defeat'
  | 'vo-game-over' | 'vo-flawless' | 'vo-combo'

export type MusicName =
  | 'theme-main' | 'battle-encounter'
  | 'battle-victory' | 'battle-defeat' | 'raid-event'

const SFX_PATHS: Record<SFXName, string> = {
  'click':           'ui/click.mp3',
  'hover':           'ui/hover.mp3',
  'modal-open':      'ui/modal-open.mp3',
  'modal-close':     'ui/modal-close.mp3',
  'nav-switch':      'ui/nav-switch.mp3',
  'notification':    'ui/notification.mp3',
  'error':           'ui/error.mp3',
  'hit-fire':        'battle/hit-fire.mp3',
  'hit-water':       'battle/hit-water.mp3',
  'hit-earth':       'battle/hit-earth.mp3',
  'hit-air':         'battle/hit-air.mp3',
  'hit-void':        'battle/hit-void.mp3',
  'hit-aether':      'battle/hit-aether.mp3',
  'super-effective': 'battle/super-effective.mp3',
  'not-effective':   'battle/not-effective.mp3',
  'ko':              'battle/ko.mp3',
  'flee':            'battle/flee.mp3',
  'creature-catch':  'game/creature-catch.mp3',
  'coin-collect':    'game/coin-collect.mp3',
  'level-up':        'game/level-up.mp3',
  'awakening':       'game/awakening.mp3',
  'evolution':       'game/evolution.mp3',
  'trade-complete':  'game/trade-complete.mp3',
  'bounty-appear':   'game/bounty-appear.mp3',
  'vo-fight':        'battle/vo-fight.mp3',
  'vo-ready':        'battle/vo-ready.mp3',
  'vo-victory':      'battle/vo-victory.mp3',
  'vo-defeat':       'battle/vo-defeat.mp3',
  'vo-game-over':    'battle/vo-game-over.mp3',
  'vo-flawless':     'battle/vo-flawless.mp3',
  'vo-combo':        'battle/vo-combo.mp3',
}

const MUSIC_PATHS: Record<MusicName, string> = {
  'theme-main':       'theme-main.mp3',
  'battle-encounter': 'battle-encounter.mp3',
  'battle-victory':   'battle-victory.mp3',
  'battle-defeat':    'battle-defeat.mp3',
  'raid-event':       'raid-event.mp3',
}
